import React, { useRef, useState } from "react";
import { ClockCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, InputNumber, Popover, Tooltip } from "antd";
import { useTranslation } from "react-i18next";

import { cn } from "@fitness-app/utils/src/styles/cn";

const PRESETS = [0, 30, 60, 90, 120, 180];
const MAX_REST_SECONDS = 1800;

interface RestGapProps {
  /** The exercise's own value; null or undefined means it follows the workout default. */
  override?: number | null;
  defaultRest: number;
  /** Without it the gap shows no rest, only the add button. */
  onChange?: (restAfterExercise: number | null) => void;
  onAdd?: () => void;
  disabled?: boolean;
}

/** The space between two exercise cards: the rest before the next exercise and adding an exercise right here. */
const RestGap = ({ override, defaultRest, onChange, onAdd, disabled }: RestGapProps) => {
  const { t } = useTranslation("workouts");
  const [open, setOpen] = useState(false);
  const isCustom = typeof override === "number";
  const rest = isCustom ? override : defaultRest;
  const [draft, setDraft] = useState<number | null>(rest);
  const inputRef = useRef<React.ComponentRef<typeof InputNumber>>(null);

  const toggle = (next: boolean) => {
    // Every opening starts from the rest currently in force, not from an abandoned edit.
    if (next) {
      setDraft(rest);
    }
    setOpen(next);
  };

  const commit = (value: number | null) => {
    setOpen(false);
    if (!onChange || value === null || value === rest) {
      return;
    }
    // Picking the default again drops the override, so a later change of the default still applies here.
    onChange(value === defaultRest ? null : value);
  };

  const content = (
    <div className="flex w-[260px] flex-col gap-3">
      <InputNumber
        ref={inputRef}
        className="w-full"
        min={0}
        max={MAX_REST_SECONDS}
        precision={0}
        value={draft}
        onChange={(value) => setDraft(typeof value === "number" ? value : null)}
        onPressEnter={() => commit(draft)}
        addonAfter={t("restGap.seconds")}
      />
      <div className="flex flex-wrap gap-1">
        {PRESETS.map((preset) => (
          <Button
            key={preset}
            size="small"
            type={preset === rest ? "primary" : "default"}
            onClick={() => commit(preset)}
          >
            {preset === 0 ? t("restGap.noRestShort") : preset}
          </Button>
        ))}
      </div>
      <div className="flex items-center justify-between">
        {isCustom ? (
          <Button
            size="small"
            type="link"
            className="px-0"
            onClick={() => {
              setOpen(false);
              onChange?.(null);
            }}
          >
            {t("restGap.reset", { seconds: defaultRest })}
          </Button>
        ) : (
          <span />
        )}
        <Button size="small" type="primary" onClick={() => commit(draft)}>
          OK
        </Button>
      </div>
    </div>
  );

  return (
    <div className="-mt-2 mb-2 flex items-center gap-2 px-2">
      <div className="h-px flex-1 bg-gray-200" />
      {onChange ? (
        <Popover
          trigger="click"
          title={t("restGap.title")}
          content={content}
          open={open && !disabled}
          onOpenChange={toggle}
          // Not autoFocus: the popover mounts before it is positioned, so focusing then scrolls the
          // page to its initial spot at the top. Focus once it is in place, without scrolling.
          afterOpenChange={(visible) => visible && inputRef.current?.focus({ preventScroll: true })}
        >
          <Button
            size="small"
            shape="round"
            disabled={disabled}
            icon={<ClockCircleOutlined />}
            className={cn(
              "text-xs",
              isCustom ? "!border-amber-300 !bg-amber-50 font-medium !text-amber-800" : "text-gray-500",
            )}
          >
            {rest > 0 ? t("restGap.rest", { seconds: rest }) : t("restGap.noRest")}
            {isCustom && <span className="font-normal text-amber-600">· {t("restGap.custom")}</span>}
          </Button>
        </Popover>
      ) : null}
      {onAdd && !disabled ? (
        <Tooltip title={t("restGap.addHere")}>
          <Button
            size="small"
            shape="circle"
            type="dashed"
            icon={<PlusOutlined />}
            aria-label={t("restGap.addHere")}
            onClick={onAdd}
          />
        </Tooltip>
      ) : null}
      <div className="h-px flex-1 bg-gray-200" />
    </div>
  );
};

export default RestGap;
